// Sugalarm — collapsed urgent banner for both urgent_low + urgent_high
// (cycle 61).
//
// History: cycle 33 demoted regular `low` to a thin top banner; cycle
// 36.1 added the symmetric `high` banner; cycle 42 carved `urgent_high`
// out of the full-bleed red <UrgentOverlay> takeover so that once the
// parent presses "Address the Situation" the Now screen returns and a
// thin `UrgentHighBanner` keeps the urgency cue visible while the
// correction proceeds. Cycle 42 explicitly *kept* `urgent_low` as the
// full-bleed takeover throughout — "acutely dangerous, must not be
// missed."
//
// Cycle 61 (per direct user direction): once **Address** has been
// pressed on an urgent low, it should collapse to the same thin
// full-width bar. Address is an explicit acknowledgement; the
// persistent red bar keeps the alarm visible without blocking the
// whole screen so the parent can watch the carb correction land.
// This file generalizes the cycle-42 `UrgentHighBanner` to handle both
// urgent levels — same ~40 px footprint, same `var(--zone-urgent)`
// red, level-aware label.
//
// Reachable states (the full takeover owns the pre-Address active
// state for both levels):
//   phase === "active" && hasSnoozed → alarm re-armed after snooze
//                                     elapsed; show Snooze + Stop
//                                     (cycle-62 two-button)
//   phase === "snoozed"              → "Snoozing M:SS" countdown text
//   stopped                          → "Urgent High · Stopped" muted
//                                     tag + subtext + Snooze (cycle 187)
//
// Cycle 187: a **Stopped** urgent now collapses here too. Through
// cycle 67 a Stopped urgent held the full-screen takeover ("acutely-
// dangerous urgent levels still hold the full screen even when
// silent"); this cycle supersedes that decision for the urgent family
// so a Stopped urgent minimizes to this thin bar exactly like a
// Snoozed one — the rest of the Now screen stays usable while the
// banner keeps the (muted) urgency cue on screen. The cycle-73
// `urgent_low` Snooze-only floor is preserved: `stop()` no-ops on
// `urgent_low`, so `episode.stopped` is only ever true for
// `urgent_high` and the Stopped branch reads "Urgent High · Stopped"
// in practice (the label stays level-aware regardless).

import { displayValue } from "../../lib/glucose";
import { fmtTreatingRemaining } from "../../lib/treating";
import { useClockMode } from "../../hooks/useAlarmClockMode";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import {
  ALARM_BANNER_PAD_Y,
  ALARM_BANNER_ROW_PX,
  alarmBannerDot,
  alarmBannerGround,
  alarmPrimaryButton,
  alarmSecondaryButton,
} from "./alarmBannerStyle";
// Cycle 452.2: the portrait-phone stacking, shared with the off-Now banner.
import stack from "./AlarmBanner.module.css";

interface Props {
  episode: AlarmEpisodeState;
  units: "mg/dL" | "mmol/L";
  // Cycle 303: mobile-compact strip drops the reading, merges the
  // status into one ellipsizing text block, and drops the long
  // "will re-alarm…" subtext so the Snooze button can't be squished
  // on a 375 px viewport. Kiosk/web byte-identical (reading + subtext
  // preserved). See LowAlarmBanner for the full rationale.
  variant?: FrameVariant;
}

function fmtSnooze(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

export function UrgentBanner({ episode, units, variant }: Props) {
  // Cycle 303: mobile-compact presentation (drop reading + subtext,
  // merge status into an ellipsizing text block).
  const mobile = variant === "mobile";
  // Cycle 394 (per direct operator direction): on the KIOSK, while the
  // bedside face is up, this strip pins FULL-WIDTH to the bottom edge —
  // the cycle-167 treatment the resting Low/High strips have had since
  // the face shipped, which the urgent family was never given.
  //
  // ⚠ THE BUG THIS FIXES IS A LAYOUT COLLISION, NOT A MISSING STYLE.
  // `.alarmRow.kiosk` is a GRID (`minmax(0,1fr) 480px`) and every mounted
  // strip is forced into column 2, so in clock mode the collapsed urgent
  // banner rendered as a ~480 px pill hanging off the TOP-RIGHT corner of
  // the face — the Snooze button pushed against the column's right edge
  // with its rounded cap shaved off. Observed on an SM-X230 during a real
  // urgent-low drill. The Low/High strips escape that grid via the
  // cycle-167 `position: fixed` override; urgent had no such escape.
  //
  // Hooks run BEFORE this component's early `return null` — the gate
  // below is a conditional return, so reading clock mode here (rather
  // than after it) is what keeps the hook order stable across renders.
  const clockModeActive = useClockMode().active;
  // Cycle 452.2: stack on a portrait phone (AlarmBanner.module.css), shared
  // with the off-Now banner — but never the bedside strip, which is fixed
  // inside the clock face's 56 px reserved band.
  const stacked = !clockModeActive;
  // Scoped to `kiosk` deliberately. The operator's direction is the kiosk
  // bedside face; web/mobile clock surfaces keep their existing chrome
  // byte-identical, so this cannot regress a phone at 3 a.m.
  const clockBottomStrip = clockModeActive && variant === "kiosk";
  // Self-gate: only an acknowledged urgent_low / urgent_high episode
  // collapses into this banner. Non-urgent levels (low / high) have
  // their own dedicated banners; pre-Address urgent state still owns
  // the full-screen takeover and returns null here.
  // Cycle 81: gate on the phase-live predicate + the persisted
  // episode level rather than the live reading's instantaneous zone,
  // so a collapsed urgent banner can never sit on screen with audio
  // armed and no control through the recovery-dwell window.
  // Cycle 187: the collapse condition now also fires on **Stopped**
  // (`episode.stopped`) — not just Snooze — so a Stopped urgent
  // minimizes here too.
  // ⚠ CYCLE 454 (operator decision): urgent low only. Urgent High is no
  // longer a takeover and never collapses here — it renders in
  // `HighAlarmBanner` (Stop only) in every state. The urgent-high branches
  // below are therefore unreachable; they are left in place rather than
  // edited, so Urgent Low's rendering is byte-for-byte what it was.
  if (
    !episode.isLive ||
    episode.episodeLevel !== "urgent_low" ||
    !(
      episode.hasSnoozed ||
      episode.phase === "snoozed" ||
      episode.stopped ||
      // Cycle 391: an open treating window collapses here too, carrying
      // its own countdown — the cycle-61 collapse-on-acknowledgement
      // precedent, applied to a declaration of presence.
      episode.treating
    ) ||
    // Cycle 304: a muted urgent that is back in range on a fresh reading
    // hides too. Cycle 398: the predicate now covers ACTIVE episodes as
    // well, so the ACTIVE urgent full-screen takeover on NowScreen — which
    // cycle 304 explicitly left alone because the old predicate could never
    // describe it — now clears on the same term. This banner's own gate is
    // unchanged in form; only what the predicate can be true FOR widened.
    //
    // ⚠ `clockUrgentStrip` in NowScreen MIRRORS this whole mount gate,
    // including this term, so the bedside face reserves its bottom band iff
    // the strip actually mounts. The two move together or the face reserves
    // dead space (or overlaps the clock digits). Re-verified this cycle.
    //
    // Server recovery is unchanged — display only.
    episode.bannerSuppressedInRange
  ) {
    return null;
  }

  // Cycle 187: Stopped takes precedence over the snooze branches (when
  // stopped, audio is muted regardless of any elapsed snooze) and
  // mirrors the muted treatment the mild Low/High banners already use
  // for Stopped — non-pulsing dimmed dot, "Urgent High · Stopped" tag,
  // the existing "Stopped — will re-alarm if it gets worse." subtext,
  // and a single Snooze (Stop is already pressed by definition).
  // Cycle 391: TREATING takes precedence over every other collapsed
  // state, because it is the most specific and the most time-bounded
  // thing that can be true about the episode right now — and because the
  // countdown is the whole point of showing it.
  const showTreating = episode.treating;
  // ⚠ CYCLE 416: a failed ack outranks the calm presentation here too. The
  // High/Low banners got this gate first and UrgentBanner was missed — on the
  // one family where Stop is floored off, so the failure notice is the only
  // thing that can tell the user the alarm is still live.
  const ackFailed = episode.ackFailure != null;
  const showStopped = !ackFailed && !showTreating && episode.stopped;
  // Cycle 62: after the 5-min snooze elapses the alarm re-arms in
  // place — show Snooze + Stop again so the user can re-snooze or
  // drop into the muted (Stopped) state.
  const showReArmedButtons =
    !showStopped &&
    !showTreating &&
    episode.phase === "active" &&
    episode.hasSnoozed;
  const showSnoozing =
    !ackFailed && !showStopped && !showTreating && episode.phase === "snoozed";
  const sgvText =
    episode.sgv != null ? displayValue(episode.sgv, units) : "—";
  const levelLabel =
    episode.episodeLevel === "urgent_low" ? "Urgent Low" : "Urgent High";

  return (
    <div
      role="status"
      aria-live="polite"
      // Cycle 187: carry the cycle-80/103 `.alarmBannerStrip` lift so
      // the collapsed urgent strip renders ABOVE any kiosk chrome
      // (z-index 12, scoped to `.kiosk` — no-op on web/mobile) exactly
      // like the mild Low/High strips, and never gets clipped at the
      // top-left corner. The shared rule also adds a legibility
      // text-shadow over a bright background.
      className={stacked ? `alarmBannerStrip ${stack.stackRow}` : "alarmBannerStrip"}
      // Cycle 452.2: an inert marker the type-floor sweep finds alarm
      // controls by (44 px buttons, 4.5:1 text).
      data-alarm-control="banner"
      style={{
        flexShrink: 0,
        // Cycle 452.2: 52 px, to carry 44 px buttons (was 40 with 32 px ones).
        // On phones the row may GROW, exactly as the Low and High banners do:
        // the shared stacking puts the buttons on their own line, and a fixed
        // height let a lone Snooze overflow onto Now's content (astra).
        ...(mobile
          ? { minHeight: ALARM_BANNER_ROW_PX, padding: `${ALARM_BANNER_PAD_Y}px 16px` }
          : { height: ALARM_BANNER_ROW_PX, padding: "0 16px" }),
        // Cycle 452.2: the darker urgent red with white ink (about 5.4:1) —
        // white on the #ff6b6b coral was 2.78:1 and read as pastel.
        ...alarmBannerGround("urgent"),
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "inherit",
        fontWeight: 700,
        // Cycle 302: smaller banner text + a single-line guarantee so the
        // longer urgent labels ("URGENT LOW · 55 mg/dL", "URGENT HIGH ·
        // 250 mg/dL") never wrap to a second line inside this fixed ~40 px
        // row — the reported bug. `white-space` is inherited, so setting
        // it on the flex container pins the label + sgv to one line (the
        // Stopped subtext already sets its own nowrap+ellipsis).
        fontSize: 13,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 0 rgba(0, 0, 0, 0.18)",
        // Cycle 452.2: the Stopped strip is no longer dimmed with `opacity`
        // (cycle 187 used 0.85) — that lowered its text's contrast. The
        // still dot and "· Stopped" carry "muted-but-watching".
        // Cycle 394: escape the `.alarmRow.kiosk` grid column and pin
        // full-width to the face's bottom edge. Byte-for-byte the
        // cycle-167 Low/High override (same properties, same z-50 —
        // below AlarmSilenceGuard 60 and UrgentOverlay 100 — and the
        // same upward shadow flip) so the three strips cannot drift
        // apart. Outside clock mode, and on web/mobile: unchanged.
        ...(clockBottomStrip
          ? {
              position: "fixed" as const,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              boxShadow: "0 -1px 0 rgba(0, 0, 0, 0.18)",
            }
          : null),
      }}
    >
      <span
        aria-hidden
        // Cycle 187: dot stops pulsing + dims in the Stopped state.
        // Cycle 391: the dot KEEPS PULSING while treating. The alarm
        // has not been dismissed — somebody is working it and the
        // window is counting down — so the strip must not read as
        // muted-and-watching the way the Stopped state does.
        style={alarmBannerDot(!showStopped)}
      />
      <span
        style={{
          textTransform: "uppercase",
          letterSpacing: mobile ? "0.06em" : "0.08em",
          // Cycle 303 (mobile): the label + merged status is the single
          // flexible, ellipsizing element so a long status can never
          // squish the Snooze/Stop cluster on a 375 px viewport.
          ...(mobile
            ? {
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
            : null),
        }}
      >
        {showStopped
          ? `${levelLabel} · Stopped`
          : showTreating
            ? // Cycle 392: the name rides the same `alert_treating` frame,
              // so the kiosk banner and every phone show it in lockstep.
              // Anonymous keeps the cycle-391 string EXACTLY — a window
              // with no name is a normal state, not a degraded one.
              `${levelLabel} · ${
                episode.treatingContactName
                  ? `${episode.treatingContactName} treating`
                  : "Treating"
              } ${fmtTreatingRemaining(episode.treatingRemainingMs ?? 0)}`
            : levelLabel}
        {/* Cycle 303 (mobile): merge the snooze countdown into the text
            block; kiosk/web keep the separate right-anchored span. */}
        {mobile && showSnoozing
          ? ` · Snoozing ${fmtSnooze(episode.snoozeRemainingMs ?? 0)}`
          : ""}
      </span>
      {/* Cycle 303: the glucose reading + the long "will re-alarm…"
          subtext are dropped on the mobile strip (the reading is on the
          Now hero; the subtext is advisory). Kiosk/web keep BOTH,
          byte-identical — the Stopped subtext still ellipsizes there. */}
      {!mobile && (
        <>
          <span style={{ opacity: 0.7 }} aria-hidden>
            ·
          </span>
          <span className="tnum">
            {sgvText} {units}
          </span>
          {/* Cycle 187: in the Stopped state the spacer carries the
              subtext (ellipsized so it never breaks the single-row kiosk
              geometry — full on a wide kiosk, gracefully clipped on a
              narrow phone); otherwise it's a plain flex spacer. */}
          {showStopped || showTreating ? (
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {showStopped
                ? "Stopped — will re-alarm if it gets worse."
                : "Holding calls and texts. Any drop ends it right away."}
            </span>
          ) : (
            <span style={{ flex: 1 }} />
          )}
        </>
      )}
      {showStopped && (
        <button
          type="button"
          onClick={() => void episode.address()}
          className={stacked ? `${stack.stackBtn} ${stack.stackSolo}` : undefined}
          style={mobile ? { ...btnStyle, flexShrink: 0 } : btnStyle}
          aria-label="Snooze (audio paused; re-arm on worse crossing)"
        >
          Snooze
        </button>
      )}
      {/* Cycle 391: the treating state KEEPS A CONTROL ON SCREEN. The
          cycle-63/81 floor yields to this banner for a collapsed urgent,
          so the banner has to carry one — and Snooze is the right one:
          it is the cycle-73 urgent-low control, it works cross-device,
          and taking it ends the window server-side (`handled_in_app`)
          rather than leaving two states to reconcile. `Stop` stays
          absent, as it is on every urgent-low surface. */}
      {showTreating && (
        <button
          type="button"
          onClick={() => void episode.address()}
          className={stacked ? `${stack.stackBtn} ${stack.stackSolo}` : undefined}
          style={mobile ? { ...btnStyle, flexShrink: 0 } : btnStyle}
          aria-label="Snooze"
        >
          Snooze
        </button>
      )}
      {showReArmedButtons && (
        <div
          className={stacked ? stack.stackActions : undefined}
          style={{
            display: "inline-flex",
            gap: 8,
            alignItems: "center",
            ...(mobile ? { flexShrink: 0 } : null),
          }}
        >
          <button
            type="button"
            onClick={() => void episode.address()}
            className={stacked ? stack.stackBtn : undefined}
            style={btnStyle}
          >
            Snooze
          </button>
          {/* Cycle 73: re-armed urgent_low offers Snooze only,
              matching the cycle-72/73 per-zone contract enforced by
              <UrgentOverlay/>. urgent_high keeps both buttons here so
              the user can drop into the muted (stopped) overlay
              state without snoozing again. */}
          {episode.episodeLevel !== "urgent_low" && (
            <button
              type="button"
              onClick={episode.stop}
              className={stacked ? stack.stackBtn : undefined}
              style={secondaryBtnStyle}
            >
              Stop
            </button>
          )}
        </div>
      )}
      {showSnoozing && !mobile && (
        <span
          className="tnum"
          style={{
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          Snoozing {fmtSnooze(episode.snoozeRemainingMs ?? 0)}
        </span>
      )}
    </div>
  );
}

// Cycle 452.2: the shared alarm buttons — 44 px tall (were 32), the urgent
// Snooze keeping its cycle-187 64 px minimum width. Order, labels and
// handlers unchanged; Urgent Low still renders no Stop (cycle 73).
const btnStyle: React.CSSProperties = alarmPrimaryButton("urgent");
const secondaryBtnStyle: React.CSSProperties = alarmSecondaryButton();
